import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ErrorBoundarySuspense } from '@/components/error-boundary-suspense';
import { Card, CardContent } from '@/components/ui/card';
import { usePurchaseOrderActivityTimeline } from '@/features/purchase-order/activity/hooks/use-purchase-order-activity-timeline';
import { GET_PURCHASE_ORDER_ACTIVITY_KEY } from '@/keys';
import { PurchaseOrderActivityCard } from './activity/components/purchase-order-activity-card';
import { PurchaseOrderActivityDetailDialog } from './activity/components/purchase-order-activity-detail-dialog';
import type { PurchaseOrderActivityDetailState } from './activity/types/purchase-order-activity-item';
import { PurchaseOrderActivitySkeleton } from './purchase-order-activity-skeleton';

type PurchaseOrderActivityProps = {
  purchaseOrderId: string;
};

export function PurchaseOrderActivity({
  purchaseOrderId,
}: PurchaseOrderActivityProps) {
  const { t } = useTranslation('purchaseOrder');

  return (
    <ErrorBoundarySuspense
      errorFallbackWrapper={
        <div className='flex h-full w-full items-center justify-center p-4' />
      }
      loadingFallback={<PurchaseOrderActivitySkeleton />}
      message={t('activity.error.message')}
      queryKey={[...GET_PURCHASE_ORDER_ACTIVITY_KEY, purchaseOrderId]}
    >
      <PurchaseOrderActivityContent purchaseOrderId={purchaseOrderId} />
    </ErrorBoundarySuspense>
  );
}

function PurchaseOrderActivityContent({
  purchaseOrderId,
}: PurchaseOrderActivityProps) {
  const { t, i18n } = useTranslation('purchaseOrder');
  const locale = i18n.language;
  const [detailState, setDetailState] =
    useState<PurchaseOrderActivityDetailState>(null);
  const { documentUrlByName, timelineActivities } =
    usePurchaseOrderActivityTimeline({
      purchaseOrderId,
    });

  if (timelineActivities.length === 0) {
    return (
      <div className='flex min-h-[240px] flex-1 items-center justify-center rounded-xl border border-dashed bg-card px-6 py-12 text-center shadow-sm'>
        <p className='text-muted-foreground'>{t('activity.empty')}</p>
      </div>
    );
  }

  return (
    <>
      <Card className='py-0'>
        <CardContent className='px-6 py-6'>
          <div className='space-y-8'>
            {timelineActivities.map((activity, index) => (
              <PurchaseOrderActivityCard
                activity={activity}
                documentUrlByName={documentUrlByName}
                isLast={index === timelineActivities.length - 1}
                key={activity.id}
                locale={locale}
                onOpenDetail={setDetailState}
              />
            ))}
          </div>
        </CardContent>
      </Card>

      <PurchaseOrderActivityDetailDialog
        onOpenChange={(open) => {
          if (!open) {
            setDetailState(null);
          }
        }}
        state={detailState}
      />
    </>
  );
}
